// src/component/Price.jsx
import React, { useEffect, useState, useMemo } from "react";
import { authFetch } from "../utils/authFetch";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaHeart,
  FaRegHeart,
  FaShoppingCart,
  FaPlus,
  FaMinus,
  FaListUl, // иконка для кнопки «Категории»
} from "react-icons/fa";
import CategoryTree from "./PriceCategoryTree";
import ProductModal from "./PriceProductModal";
import SafeImage from "./ui/SafeImage";

/* ----------------------------------------
   ВСПОМОГАТЕЛЬНОЕ
----------------------------------------- */

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const itemsToUiMap = (items = []) => {
  const map = {};
  items.forEach((it) => {
    const pid = it.product ?? it.product_id;
    if (pid != null) map[pid] = Number(it.quantity) || 0;
  });
  return map;
};

const fmt = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : Number(n).toLocaleString("ru-RU");

/* ----------------------------------------
   КОМПОНЕНТ КАТАЛОГА
----------------------------------------- */
export default function Catalog() {
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [modalProduct, setModalProduct] = useState(null);

  const [favorites, setFavorites] = useState([]); // product_id[]
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [cartUi, setCartUi] = useState({}); // { [productId]: qty }

  const [isMobile, setIsMobile] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);

  const navigate = useNavigate();
  const query = useQuery();
  const categoryFromURL = query.get("category");
  const productFromURL = query.get("product");
  const searchQ = query.get("q");

  /* ----------------------------------------
     АДАПТИВ
  ----------------------------------------- */
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    handler();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    if (isMobile) setMobileCategoriesOpen(false);
  }, [isMobile]);

  // Блокируем скролл при открытом drawer категорий
  useEffect(() => {
    if (!isMobile) return;
    document.body.style.overflow = mobileCategoriesOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, mobileCategoriesOpen]);

  // Блокируем скролл при открытой модалке товара
  useEffect(() => {
    if (!modalProduct) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev);
  }, [modalProduct]);

  /* ----------------------------------------
     ПОЛЕЗНЫЕ УТИЛИТЫ
  ----------------------------------------- */
  const getImgSrc = (p) => {
    const img = p?.image;
    if (!img) return null;
    return img.startsWith?.("http") ? img : `/media/${img}`;
  };

  const getMinPrice = (p) => {
    const stores = Array.isArray(p?.stores) ? p.stores : [];
    const prices = stores
      .filter((s) => s.price != null)
      .map((s) => Number(s.price));
    return prices.length ? Math.min(...prices) : null;
  };

  const getTotalQty = (p) => {
    const stores = Array.isArray(p?.stores) ? p.stores : [];
    return stores.reduce((acc, s) => acc + (Number(s.quantity) || 0), 0);
  };

  const sortedProducts = useMemo(() => {
    return [...(products || [])].sort((a, b) => {
      const qa = getTotalQty(a);
      const qb = getTotalQty(b);
      const pa = qa > 1 ? 0 : qa === 1 ? 1 : 2;
      const pb = qb > 1 ? 0 : qb === 1 ? 1 : 2;
      if (pa !== pb) return pa - pb;
      const ma = getMinPrice(a) ?? Number.POSITIVE_INFINITY;
      const mb = getMinPrice(b) ?? Number.POSITIVE_INFINITY;
      return ma - mb;
    });
  }, [products]);

  /* ----------------------------------------
     ПОИСК
  ----------------------------------------- */
  useEffect(() => {
    if (!searchQ) return;
    fetch(`/api/catalog/products/search/?q=${encodeURIComponent(searchQ)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        setProducts(Array.isArray(data) ? data : []);
        setActiveCategory(null);
        setModalProduct(null);
      })
      .catch(() => setProducts([]));
  }, [searchQ]);

  /* ----------------------------------------
     КАТЕГОРИИ
  ----------------------------------------- */
  useEffect(() => {
    fetch("/api/catalog/categories/")
      .then((r) => r.json())
      .then((d) => setCategories(Array.isArray(d) ? d : []))
      .catch(() => setCategories([]));
  }, []);

  /* ----------------------------------------
     ТОВАРЫ ПО КАТЕГОРИИ
  ----------------------------------------- */
  useEffect(() => {
    if (!categoryFromURL || categoryFromURL === "undefined") return;
    setActiveCategory(Number(categoryFromURL));
    fetch(`/api/catalog/products/?category_id=${categoryFromURL}`)
      .then((r) => r.json())
      .then((d) => setProducts(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [categoryFromURL]);

  /* ----------------------------------------
     ИЗБРАННОЕ
  ----------------------------------------- */
  useEffect(() => {
    authFetch("/api/catalog/favorites/")
      .then(({ data, status }) => {
        if (status === 401) setFavorites([]);
        else
          setFavorites(
            Array.isArray(data) ? data.map((f) => f.product_id) : []
          );
      })
      .catch(() => setFavorites([]));
  }, []);

  /* ----------------------------------------
     СИНХРОН КОРЗИНЫ
  ----------------------------------------- */
  useEffect(() => {
    let ignore = false;
    authFetch("/api/orders/cart/")
      .then(({ data, status }) => {
        if (ignore) return;
        if (status === 200 && Array.isArray(data))
          setCartUi(itemsToUiMap(data));
        else setCartUi({});
      })
      .catch(() => !ignore && setCartUi({}));
    return () => {
      ignore = true;
    };
  }, []);

  /* ----------------------------------------
     АВТООТКРЫТИЕ МОДАЛКИ
  ----------------------------------------- */
  useEffect(() => {
    if (!productFromURL || products.length === 0) return;
    const found = products.find((p) => String(p.id) === String(productFromURL));
    if (found) setModalProduct(found);
  }, [productFromURL, products]);

  /* ----------------------------------------
     КОРЗИНА
  ----------------------------------------- */
  const handleAddToCart = async (productId, qty = 1) => {
    const p = products.find((x) => x.id === productId);
    const available = p ? getTotalQty(p) : 0;
    const uiCurrent = cartUi[productId] || 0;

    if (available <= 0) return false;
    if (uiCurrent + qty > available) {
      alert("Нельзя добавить больше, чем есть в наличии.");
      return false;
    }

    const { data, status } = await authFetch("/api/orders/cart/add/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_id: productId, quantity: qty }),
    });

    if (status === 401) {
      alert("Войдите или зарегистрируйтесь!");
      return false;
    }
    if (status === 409) {
      alert((data && data.error) || "Недостаточно товара на складе.");
      return false;
    }
    if (status < 200 || status >= 300) {
      alert("Не удалось добавить товар. Попробуйте позже.");
      return false;
    }

    if (Array.isArray(data)) setCartUi(itemsToUiMap(data));
    else
      setCartUi((prev) => ({
        ...prev,
        [productId]: (prev[productId] || 0) + qty,
      }));

    return true;
  };

  const handleDecreaseCartUi = async (productId) => {
    try {
      const { data: items, status } = await authFetch("/api/orders/cart/");
      if (status === 401) {
        alert("Чтобы изменить корзину, войдите в аккаунт.");
        return;
      }

      const item = (items || []).find(
        (it) => String(it.product ?? it.product_id) === String(productId)
      );

      if (!item) {
        setCartUi((prev) => {
          const copy = { ...prev };
          delete copy[productId];
          return copy;
        });
        return;
      }

      const newQty = Number(item.quantity) - 1;

      if (newQty <= 0) {
        await authFetch("/api/orders/cart/delete/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ item_id: item.id }),
        });
      } else {
        await authFetch("/api/orders/cart/update/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ item_id: item.id, quantity: newQty }),
        });
      }

      const { data: after } = await authFetch("/api/orders/cart/");
      setCartUi(itemsToUiMap(after || []));
    } catch {
      // не валимся UI-но
    }
  };

  /* ----------------------------------------
     ИЗБРАННОЕ
  ----------------------------------------- */
  const toggleFavorite = async (productId) => {
    try {
      if (favorites.includes(productId)) {
        await authFetch(`/api/catalog/favorites/${productId}/`, {
          method: "DELETE",
        });
      } else {
        await authFetch(`/api/catalog/favorites/${productId}/`, {
          method: "POST",
        });
      }
      const { data } = await authFetch("/api/catalog/favorites/");
      setFavorites(Array.isArray(data) ? data.map((f) => f.product_id) : []);
    } catch {
      alert("Ошибка изменения избранного.");
    }
  };

  /* ----------------------------------------
     КЛИК ПО КАТЕГОРИИ
  ----------------------------------------- */
  const handleCategoryClick = (categoryId) => {
    if (!categoryId || categoryId === "undefined") return;

  // Готовим новый querystring
  const params = new URLSearchParams(window.location.search);
  if (activeCategory === categoryId) {
    // Повторный клик — убираем категорию из URL
    setActiveCategory(null);
    setProducts([]);
    params.delete("category");
    params.delete("product");
    params.delete("q");
    navigate(`?${params.toString()}`, { replace: false });
    return;
  }

    fetch(`/api/catalog/products/?category_id=${categoryId}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        setActiveCategory(categoryId);
        setProducts(Array.isArray(data) ? data : []);
        if (!isMobile) window.scrollTo({ top: 0, behavior: "smooth" });
      // В URL фиксируем выбранную категорию, очищаем поиск/продукт
      params.set("category", String(categoryId));
      params.delete("product");
      params.delete("q");
      navigate(`?${params.toString()}`, { replace: false });
      })
      .catch(() => setProducts([]));
  };

  const closeModal = () => {
    setModalProduct(null);
    query.delete("product");
    const qs = query.toString();
    navigate(qs ? `?${qs}` : "."); // вернёмся на /price без лишнего "?"
  };

  /* ----------------------------------------
     РЕНДЕР КАРТОЧКИ ТОВАРА — pastel / rounded
  ----------------------------------------- */
  const renderCard = (product) => {
    const isFav = favorites.map(String).includes(String(product.id));
    theMinPrice: {
    }
    const minPrice = getMinPrice(product);
    const totalQty = getTotalQty(product);
    const isOut = totalQty <= 0;

    const uiQty = cartUi[product.id] || 0;
    const showCartControls = uiQty > 0 || hoveredProductId === product.id;
    const canAddMore = uiQty < totalQty;

    return (
      <div
        key={product.id}
        className="group relative flex flex-col rounded-2xl border border-neutral-200 bg-white/90 p-4 shadow-sm ring-1 ring-transparent transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:ring-orange-200"
        onClick={() => {
          setModalProduct(product);
          if (activeCategory)
            navigate(`?category=${activeCategory}&product=${product.id}`);
          else navigate(`?product=${product.id}`);
        }}
        onMouseEnter={() => setHoveredProductId(product.id)}
        onMouseLeave={() => setHoveredProductId(null)}
        onTouchStart={() => {
          setHoveredProductId(product.id);
          setTimeout(() => setHoveredProductId(null), 2000);
        }}
      >
        {/* избранное — скрыто по умолчанию, видно на hover карточки; всегда видно, если уже в избранном */}
        <button
          type="button"
          className={`absolute right-3 top-3 z-10 rounded-full bg-white/80 p-2 shadow-sm backdrop-blur
              hover:bg-white transition-opacity duration-200
              ${
                isFav || hoveredProductId === product.id
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
              }`}
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite(product.id);
          }}
          aria-label={isFav ? "Убрать из избранного" : "В избранное"}
        >
          {isFav ? (
            <FaHeart className="text-red-500" />
          ) : (
            <FaRegHeart className="text-neutral-500" />
          )}
        </button>

        {/* изображение — фон белый */}
        <div className="mb-3 overflow-hidden rounded-xl bg-white">
          <div className="aspect-[4/3] w-full">
            <SafeImage
              src={getImgSrc(product)}
              alt={product.name}
              className="h-full w-full select-none object-contain p-4"
              fallback="/no-image.svg"
            />
          </div>
        </div>

        {/* название */}
        <h3 className="line-clamp-2 text-[15px] font-semibold text-neutral-800">
          {product.name}
        </h3>

        {/* краткая инфа по магазинам */}
        {product.stores?.length > 0 && (
          <div className="mt-2 space-y-0.5 text-xs text-neutral-600">
            {product.stores
              .filter((s) => s.quantity > 0)
              .slice(0, 3)
              .map((s) => (
                <div key={s.store}>
                  {s.store_name}: {s.quantity} шт
                  {s.price ? ` — ${fmt(s.price)} ₽` : ""}
                </div>
              ))}
          </div>
        )}

        {/* низ карточки */}
        <div className="mt-3 flex items-end justify-between">
          <div>
            <div className="inline-flex items-center rounded-full bg-orange-50 px-3 py-1 text-sm font-semibold text-orange-700">
              {minPrice != null ? `${fmt(minPrice)} ₽` : "—"}
            </div>
            <div
              className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-xs ${
                isOut
                  ? "bg-red-50 text-red-600 ring-1 ring-red-100"
                  : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
              }`}
            >
              {isOut ? "Нет в наличии" : `Всего: ${fmt(totalQty)} шт.`}
            </div>
          </div>

          {/* контролы корзины */}
          <div
            className={`flex items-center gap-2 transition-opacity ${
              showCartControls
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100"
            }`}
          >
            {isOut ? (
              <button
                className="cursor-not-allowed rounded-full bg-neutral-300 p-2 text-white"
                title="Нет в наличии"
                disabled
                onClick={(e) => e.stopPropagation()}
              >
                <FaShoppingCart />
              </button>
            ) : (cartUi[product.id] || 0) > 0 ? (
              <div
                className="flex items-center gap-2 rounded-full bg-emerald-600 px-3 py-1 text-white"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="rounded-full bg-emerald-700 p-1 hover:bg-emerald-800"
                  onClick={() => handleDecreaseCartUi(product.id)}
                  aria-label="–"
                >
                  <FaMinus size={10} />
                </button>
                <span className="min-w-[16px] text-center text-sm">
                  {cartUi[product.id] || 0}
                </span>
                <button
                  className="rounded-full bg-emerald-700 p-1 hover:bg-emerald-800 disabled:opacity-50"
                  disabled={!canAddMore}
                  onClick={() => handleAddToCart(product.id, 1)}
                  aria-label="+"
                  title={canAddMore ? "Добавить ещё" : "Достигнут остаток"}
                >
                  <FaPlus size={10} />
                </button>
              </div>
            ) : (
              <button
                className="rounded-full bg-emerald-600 p-2 text-white hover:bg-emerald-700"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddToCart(product.id, 1);
                }}
                title="В корзину"
              >
                <FaShoppingCart />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ----------------------------------------
     РЕНДЕР СТРАНИЦЫ
  ----------------------------------------- */
  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Пастельная «шапка» */}
      <div className="sticky top-0 z-20">
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r " />
          <div className="relative mx-auto max-w-screen-xl px-4 py-4">
            <h1 className="text-2xl font-bold tracking-tight text-neutral-800">
              Каталог товаров
            </h1>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-screen-xl px-4 py-6 pb-24">
        <div className="flex flex-col gap-6 md:flex-row">
          {/* На мобильных aside больше не показываем — он в drawer */}
          {!isMobile && (
            <aside className="w-full self-start md:sticky md:top-24 md:w-1/4">
              <div className="rounded-xl border border-neutral-200 bg-white/80 p-4 shadow-sm backdrop-blur max-h-[calc(100vh-8rem)] overflow-y-auto">
                <h2 className="mb-3 text-lg font-semibold text-neutral-700">
                  Категории
                </h2>
                <CategoryTree
                  categories={categories}
                  onCategoryClick={handleCategoryClick}
                  activeCategory={activeCategory}
                />
              </div>
            </aside>
          )}

          <main className="flex-1">
            {products && products.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {sortedProducts.map(renderCard)}
              </div>
            ) : activeCategory ? (
              <div className="mt-10 text-center text-neutral-500">
                Нет товаров в этой категории
              </div>
            ) : (
              <div className="mt-10 text-center text-neutral-500">
                Выберите категорию, чтобы посмотреть товары
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Плавающая кнопка «Категории» — ненавязчивая пилюля (моб.) */}
      {isMobile && !mobileCategoriesOpen && (
        <button
          type="button"
          onClick={() => setMobileCategoriesOpen(true)}
          className="fixed left-3 top-20 z-40 md:hidden inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1.5 text-sm text-neutral-700 ring-1 ring-neutral-300 shadow-sm backdrop-blur hover:bg-white/90 active:scale-95"
          aria-label="Открыть категории"
          title="Категории"
        >
          <FaListUl />
          Категории
        </button>
      )}

      {/* Drawer категорий на мобильных */}
      {isMobile && mobileCategoriesOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileCategoriesOpen(false)}
          />
          <div
            className="absolute left-0 top-0 h-full w-5/6 max-w-sm translate-x-0 transform bg-white shadow-xl transition-transform duration-300"
            role="dialog"
            aria-label="Категории"
          >
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="text-base font-semibold text-neutral-800">
                Категории
              </h2>
              <button
                onClick={() => setMobileCategoriesOpen(false)}
                className="rounded-md px-2 py-1 text-sm text-neutral-600 hover:bg-neutral-100"
                aria-label="Закрыть"
              >
                Закрыть
              </button>
            </div>
            <div className="h-[calc(100%-52px)] overflow-y-auto p-4">
              <CategoryTree
                categories={categories}
                onCategoryClick={(id) => {
                  handleCategoryClick(id);
                  setMobileCategoriesOpen(false);
                }}
                activeCategory={activeCategory}
              />
            </div>
          </div>
        </div>
      )}

      {/* модалка товара */}
      {modalProduct && (
        <ProductModal
          open={!!modalProduct}
          onClose={closeModal}
          product={modalProduct}
          cartQty={cartUi[modalProduct.id] || 0}
          onAddToCart={(qty) => handleAddToCart(modalProduct.id, qty)}
          onDecreaseOne={() => handleDecreaseCartUi(modalProduct.id)}
          isFavorite={favorites.includes(modalProduct.id)}
          onToggleFavorite={() => toggleFavorite(modalProduct.id)}
        />
      )}
    </div>
  );
}
